import React, { useEffect, useState } from 'react';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Tooltip, Button, notification, Checkbox, Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { LocationRequest } from '@app/models/locations/location-request.model';
import { storeLocation, showLocation, editLocation, updateLocation, createLocation } from '@app/api/locations.api';
import StatusBar from '@app/components/statusBar/statusBar';
import { actionButtons, getButtonsByStatus } from '@app/components/statusBar/actionButtons';
import ProductPricing from '../ProductPricing/ProductPricingForm';
import ProductPricingDetails from '../ProductPricing/ProductPricingDetails';
import { notificationController } from '@app/controllers/notificationController';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { useTranslation } from 'react-i18next';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface LocationFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  showEditIcon?: boolean;
  pageTitle: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const LocationForm: React.FC<LocationFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [typesOptions, setTypesOptions] = useState<{ label: string; value: string }[]>([]);
  const [locationCode, setLocationCode] = useState('');
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const locationId = id ? parseInt(id) : null;

  const setApiData = (res: any) => {
    setCurrentBreadcrumName(res?.location?.code ?? 'New');
    setLocationCode(res?.location?.code ?? 'New');

    form.setFieldsValue({
      code: res?.location?.code,
      name: res?.location?.name,
      description: res?.location?.description,
      status:
        type == 'view'
          ? res?.status_options?.find((e: any) => e?.value == res?.location?.status)?.label
          : res?.location?.status,
      type:
        type == 'view'
          ? res?.type_options?.find((e: any) => e?.value == res?.location?.type)?.label
          : res?.location?.type,
      address: res?.location?.address,
    });
    setStatus(res.location.status);
  };

  const setOptions = (res: any) => {
    const mappedStatuses = res.status_options.map((status: any) => ({
      label: status.label,
      value: status.value,
    }));
    setStatusOptions(mappedStatuses);

    // Update the types options if needed
    const mappedTypes = res.type_options.map((type: any) => ({
      label: type.label,
      value: type.value,
    }));
    setTypesOptions(mappedTypes); // Update the typesOptions state
  };

  useEffect(() => {
    setLoading(true);
    if (locationId) {
      if (type === 'view') {
        showLocation(locationId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to location', description: err.message });
            console.error(err);
          });
      } else if (type === 'edit') {
        editLocation(locationId)
          .then((res: any) => {
            setApiData(res);
            setOptions(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit location', description: err.message });
            console.error(err);
          });
      }
    } else if (type === 'create') {
      createLocation()
        .then((res: any) => {
          setOptions(res);
          setTypesOptions(res.type_options);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch categories', description: err.message });
          console.error(err);
        });
    }
  }, []);

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/locations')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-location`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  const handleSubmit = (values: LocationRequest) => {
    if (editable) {
      const submissionData = {
        ...values,
        ...values,
      };

      if (type === 'create') {
        storeLocation(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-location`, { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && locationId) {
        updateLocation(locationId, submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-location`, { state: { id: id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
    console.log('Form Submitted');
  };

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol span={24}>
          <StatusBar id={locationCode} status={status} />
        </BaseCol>
        <BaseCol span={24}>
          <BaseCard
            id="LocationForm"
            title={pageTitle}
            padding="1.25rem"
            // extra={
            //   type === 'view' && (
            //     <Tooltip title="Edit">
            //       <Button type="ghost" onClick={() => navigate(`/edit-location`, { state: { id: id } })}>
            //         Edit
            //       </Button>
            //     </Tooltip>
            //   )
            // }
          >
            <Form
              form={form}
              name="LocationForm"
              onFinish={handleSubmit}
              labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
              labelAlign="left"
              labelWrap
              wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
              size="small"
            >
              <BaseRow gutter={[16, 16]}>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item
                    name="code"
                    label="Item Code"
                    rules={[{ required: editable, message: 'Location Code is required' }]}
                  >
                    <BaseInput placeholder="Enter Location Code" readOnly={!editable} />
                  </Form.Item>
                </BaseCol>

                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item name="name" label="Name" rules={[{ required: editable, message: 'Name is required' }]}>
                    <BaseInput placeholder="Enter Location Name" readOnly={!editable} />
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: editable, message: 'Status is required' }]}
                  >
                    {editable ? (
                      <BaseSelect placeholder="Select Status" options={statusOptions} />
                    ) : (
                      <BaseInput readOnly={!editable} />
                    )}
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item label="Type" name="type" rules={[{ required: editable, message: 'Type is required' }]}>
                    {editable ? (
                      <BaseSelect placeholder="Select Type" options={typesOptions} />
                    ) : (
                      <BaseInput readOnly={!editable} />
                    )}
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item
                    label="Address"
                    name="address"
                    rules={[{ required: editable, message: 'Address is required' }]}
                  >
                    {editable ? <BaseInput.TextArea placeholder="Enter Address" /> : <BaseInput readOnly={!editable} />}
                  </Form.Item>
                </BaseCol>
                <BaseCol xs={24} md={24} lg={12}>
                  <Form.Item label="Description" name="description">
                    {editable ? (
                      <BaseInput.TextArea placeholder="Enter Description" />
                    ) : (
                      <BaseInput readOnly={!editable} />
                    )}
                  </Form.Item>
                </BaseCol>
              </BaseRow>

              <br />
            </Form>
          </BaseCard>
        </BaseCol>
      </BaseRow>
    </>
  );
};

export default LocationForm;
